import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@shadcn-in-nx/ui/button";
import { FileText, Video, Plus, ExternalLink } from "lucide-react";

const pickInitialLocale = (item, preferred) => {
    if (!item.locales?.length) return null;
    if (preferred && item.locales.includes(preferred)) return preferred;
    if (item.defaultLocale && item.locales.includes(item.defaultLocale)) return item.defaultLocale;
    if (item.locales.includes("en")) return "en";
    return item.locales[0];
};

export default function ResourceLibraryItem({ item, onInsert, customerLocale }) {
    const { t } = useTranslation();
    const Icon = item._type === "video" ? Video : FileText;
    const tone = item._type === "video" ? "text-red-500 bg-red-50" : "text-blue-500 bg-blue-50";

    const [locale, setLocale] = useState(() => pickInitialLocale(item, customerLocale));
    useEffect(() => { setLocale(pickInitialLocale(item, customerLocale)); }, [item, customerLocale]);

    const { title, description, url } = useMemo(() => {
        if (locale && item.urls) {
            return {
                title: item.titles?.[locale] || item.title,
                description: item.descriptions?.[locale] || item.description,
                url: item.urls?.[locale] || item.url,
            };
        }
        return { title: item.title, description: item.description, url: item.url };
    }, [item, locale]);

    return (
        <li className="group flex items-start gap-2 sm:gap-3 p-2 sm:p-2.5 bg-white border rounded-lg hover:border-blue-300 hover:shadow-sm transition-all">
            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded flex items-center justify-center flex-shrink-0 ${tone}`}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{title}</p>
                {description && <p className="text-xs text-muted-foreground line-clamp-2">{description}</p>}
                {item.locales?.length > 1 && (
                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        {item.locales.map((loc) => {
                            const isPreferred = loc === customerLocale;
                            const isActive = loc === locale;
                            return (
                                <button key={loc} type="button" onClick={(e) => { e.stopPropagation(); setLocale(loc); }}
                                    title={isPreferred ? t("admin.support.localeIsCustomer", "Customer's language") : undefined}
                                    className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide rounded border transition-colors ${
                                        isActive ? "bg-blue-500 text-white border-blue-500" : "bg-white text-muted-foreground border-border hover:border-blue-300"
                                    } ${isPreferred && !isActive ? "ring-1 ring-amber-300" : ""}`}>
                                    {loc}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                <Button size="sm" variant="default" className="h-7 px-2 text-xs" onClick={() => onInsert({ ...item, title, url })}
                    title={t("admin.support.insertIntoReply", "Insert link into reply")}>
                    <Plus className="w-3.5 h-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">{t("admin.support.insert", "Insert")}</span>
                </Button>
                <a href={url} target="_blank" rel="noopener noreferrer"
                    title={t("admin.support.openExternal", "Open in new tab")}
                    className="h-7 w-7 inline-flex items-center justify-center rounded-md border hover:bg-accent transition-colors">
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                </a>
            </div>
        </li>
    );
}
